import users from './en/users';
import auth from '@/translations/en/auth';
import sites from '@/translations/en/sites/sites';
import routes from '@/translations/en/routes';
import projects from '@/translations/en/projects/projects';
import navigation from '@/translations/en/navigation';
import organisationDetails from '@/translations/en/organisation-details';
import vendors from '@/translations/en/vendors';
import carts from '@/translations/en/carts';
import accounts from '@/translations/en/accounts';
import orders from '@/translations/en/orders/orders';
import claims from '@/translations/en/claims/claims';
import dashboard from '@/translations/en/dashboard';
import compliance from '@/translations/en/compliance';
import clients from '@/translations/en/clients';
import roles from '@/translations/en/roles';
import returns from '@/translations/en/returns';
import paymentRequest from '@/translations/en/payment-request';
import boqActionBar from '@/translations/en/boq-action-bar';
import buildBoq from '@/translations/en/build-boq';
import analytics from '@/translations/en/analytics';
import variations from '@/translations/en/variations';
import activityLog from '@/translations/en/activity-log';
import inventory from '@/translations/en/inventory';
import vendorPayments from '@/translations/en/vendor-payments';
import capabilities from '@/translations/en/capabilities';
import roleCapabilities from '@/translations/en/role-capabilities';
import provisionalBudgets, { provisionalBudgetsForms } from '@/translations/en/provisional-budgets';
import boqFrame from '@/translations/en/boq-frame';

export default {
  routes,
  general: {
    add: 'Add {value}',
    remove: 'Remove {value}',
    select: 'Select {value}',
    note: 'Note: {value}',
    dismiss: 'Dismiss',
    selectAnOption: 'Select an option',
    noResults: 'No results',
    noResultsFilter: 'There are no results matching the current criteria',
    loadMore: 'Load more',
    logout: 'Log out',
    all: 'All',
    refresh: 'Refresh',
    selectAll: 'Select all',
    actions: 'Actions',
    noPermission: {
      line1: 'You don’t have permission to view this page.',
      line2: 'Please contact your organisation admin if this appears incorrect.',
    },
    optional: '(Optional)',
    public: 'Public',
  },
  support: 'Please contact us at {email} should you have any queries.',
  pageTitles: {
    notFound: 'Page not found',
    serverError: 'Server error',
    designSystem: 'Design System',
    permissionsMatrix: 'Permissions matrix',
  },
  auth,
  projects,
  navigation,
  organisationDetails,
  users,
  sites,
  vendors,
  carts,
  accounts,
  orders,
  claims,
  inventory,
  vendorPayments,
  capabilities,
  roleCapabilities,
  provisionalBudgets,
  boqFrame,
  dashboard,
  compliance,
  clients,
  roles,
  returns,
  paymentRequest,
  boqActionBar,
  buildBoq,
  analytics,
  variations,
  activityLog,
  address: {
    line1: 'Address line 1',
    line2: 'Suburb',
    line3: 'Unit/Complex no. (Optional)',
    city: 'City {optional}',
    stateProvince: 'Province',
    country: 'Country {optional}',
    postalCode: 'Postal code',
    suburb: 'Suburb {optional}',
    addressLine1: 'Address line 1 {optional}',
  },
  components: {
    pages: {
      notFound: {
        title: 'Page not found',
        subTitle: 'The page you are looking for does not exist',
        returnHome: 'Return home',
        reloadPage: 'Reload page',
      },
      serverError: {
        title: 'Server error',
        subTitle: 'An error has occurred on the server',
        returnHome: 'Return home',
        goBack: 'Go back',
        sorry: 'Sorry for the inconvenience',
      },
      maintenance: {
        title: 'Down for maintenance',
        subtitle: `We're performing some scheduled maintenance to improve your experience.`,
        content: 'Please check back later.',
      },
      noUserOrgs: {
        title: 'No Organisation Access',
        info: `You're not currently a member of any organisation.`,
        subtitle: 'What can you do?',
        option1: 'If you believe this is a mistake, please contact your organisation administrator',
        option2: 'Check your email for any pending organisation invitations',
      },
      noProjectsOrSites: {
        title: 'No Access Yet',
        description:
          'You have not been added to any projects or sites yet. If you believe this is an error, please contact your system administrator.',
      },
      noPermissionsToView: {
        title: 'No Permission',
        description:
          'You do not have permission to view this page. Please contact your system administrator if you believe this is an error.',
      },
    },
    versionConflictDialog: {
      title: 'Page out of date',
      content:
        'The record you tried to update has been changed by someone else. Please refresh the page to get the latest version before making changes.',
      reload: 'Refresh page',
    },
    networkRetryDialog: {
      title: 'Attempting to reconnect',
      content:
        'This page seems to be taking longer than intended to load. \nPlease wait while we retry.',
      attempt: 'Attempt {attempt} of {maxAttempts}',
      failureTitle: 'Failed to reconnect',
      failureContent: 'Please check your internet connection, and reload the page.',
      reload: 'Reload page',
    },
  },
  utilities: {
    copied: 'Copied!',
    loading: 'Loading',
    cancel: 'Cancel',
    noCancel: 'No, cancel',
    confirm: 'Confirm',
    yesConfirm: 'Yes, confirm',
    yesDelete: 'Yes, delete',
    yesApprove: 'Yes, approve',
    clearFilters: 'Clear filters',
    filters: 'Filters',
    apply: 'Apply',
    modals: {
      closeButton: 'modal close button',
      confirmModal: 'Confirm modal',
      unsavedChanges: {
        title: 'Unsaved changes',
        content: 'You have unsaved changes. Are you sure you want to navigate away from this page?',
        confirm: 'Discard & leave',
        cancel: 'Cancel & stay',
      },
    },
    continue: 'Continue',
    complete: 'Complete',
    create: 'Create',
    next: 'Next',
    skip: 'Skip',
    upload: 'Upload',
    download: 'Download',
    downloadAll: 'Download all',
    submit: 'Submit',
    view: 'View',
    save: 'Save',
    yes: 'Yes',
    no: 'No',
    okay: 'Okay',
    goBack: 'Go back',
    back: 'Back',
    add: 'Add',
    update: 'Update',
    documentUpload: 'Document upload in progress...',
    documentUploadComplete: 'Document upload complete',
    excluded: 'Excluded',
    included: 'Included',
    search: 'Search',
  },
  forms: {
    file: {
      overwrite: {
        title: 'Duplicate file names',
        p1: '1 or more files have the same name as a file already in the list. Do you want to overwrite the existing file(s)?',
      },
    },
    required: '(Required)',
    reason: {
      required: 'Add a reason.',
      max: 'Use {max} characters or fewer.',
    },
    provisionalBudgets: provisionalBudgetsForms,
    auth: {
      validations: {
        required: {
          name: 'Name is required',
          status: 'Status is required',
          email: 'Email is required',
          password: 'Password is required',
          firstname: 'First name is required',
          lastname: 'Last name is required',
          organisation: 'Organisation is required',
          role: 'Role is required',
          project: 'Project is required',
          site: 'Site is required',
          city: 'City is required',
          country: 'Country is required',
          suburb: 'Suburb is required',
          startDate: 'Commencement date is required',
          endDate: 'Completion date is required',
          deliveryDate: 'Delivery date is required',
          orderItems: 'Add at least one item to this order',
          claimItems: 'Add at least one item to this claim',
          orderItemQuantity: 'Every item needs a quantity greater than zero',
          orderItemRate: 'Every item needs a rate greater than zero',
          orderVariationName: 'Every new line needs a name',
          orderVariationItem: 'Every new line needs an item selected',
          orderVariationSection: 'Every new line needs a BOQ section',
          orderItemVendor: 'Every item needs a vendor, remove any line without one',
          orderPaymentMethod: 'Payment method required',
          orderDepositPercent: 'Enter a deposit from 0.01% to 99.99% of the order',
          claimDepositPercent: 'Enter a deposit from 0.01% to 99.99% of the claim',
          claimRetentionPercent:
            'A retention has to be more than nothing and less than the whole claim',
          claimSharesTotal: 'The deposit and retentions have to leave something to pay',
          claimContraChargeAmount: 'Every contra charge needs an amount greater than zero',
          claimItemContraChargeFigures:
            'Every item contra charge needs a quantity and a rate greater than zero',
          claimContraChargesExceedTotal: "Contra charges exceed this vendor's claim",
          claimDeductionsTotal: 'The deductions have to leave something to pay',
          general: 'This field is required',
          legalCheckBox:
            'Please accept the EULA, terms and conditions and privacy policy to continue',
          siteShortCode: 'Site short code is required',
        },
        length: {
          max: 'Maximum length is {length} characters',
          min: 'Minimum length is {length} characters',
          minAndMax: 'Must be between {min} and {max} characters',
          total: 'Must be {length} digits long',
        },
        percentage: 'Must be between 0 and 100',
        uppercase: 'Must contain at least 1 uppercase character',
        lowercase: 'Must contain at least 1 lowercase character',
        special: 'Must contain at least 1 special character',
        type: {
          email: 'Email address must be a valid email address',
          number: 'Must be a number',
          positiveNumber: 'Must be a positive number',
          numberSlashed: 'Must start and end with numbers. Slashes may be used between numbers',
          vat: 'Valid VAT numbers start with a 4 and must be 10 digits long',
          registrationNo: 'Must match the following format: 0000/000000/00',
          BOQBudgetPercentage: 'Percentage must be 100 or less.',
          name: 'Name cannot include any numbers or special characters',
          defaultMarkupMax: 'Default markup cannot be more than 100,000%',
          defaultMarkupMaxBOQ: 'Cannot exceed 100,000%',
          siteShortCode: 'Site short code must be {min} or {max} alphanumeric characters.',
        },
        siteArchived: 'This site is archived and cannot be ordered against. Choose another site.',
        startDateAfterToday: 'Commencement date must be after today',
        endDateAfterStartDate: 'Completion date must be after commencement date',
      },
    },
  },
  error: {
    toast: {
      // The API refusing to end an order a payment awaiting its proof holds (D-30) is expected.
      awaitingProofHold: {
        title: 'Payment awaiting proof',
      },
      permissionsChanged: {
        title: 'Your access has changed',
        description: 'You no longer have permission to do that. Please try again.',
      },
      validation: {
        catchAll: {
          title: 'An error has occurred',
          description: 'Please try again',
        },
        sessionExpired: {
          title: 'Session expired',
          description: 'Please log in again',
        },
        loginFailed: {
          title: 'Login failed',
        },
        invalidCredentials: {
          title: 'Invalid email or password',
          // Context: a locked account gets this same refusal, since naming the lock would confirm
          // the address has an account. A reset clears the lock.
          description:
            'After several failed attempts, sign-in is paused for a while. Reset your password to get back in straight away.',
        },
        validationFailed: {
          title: 'Validation failed',
          description: 'Please ensure you have the correct link and try again',
        },
        emailAlreadyExists: {
          title: 'Email exists',
          description: 'A user with this email already exists',
        },
        vendorRemoved: {
          title: 'Vendor removed',
        },
        invalidItem: 'Invalid item',
        orderItemVariation: {
          title: 'Variation(s) pending',
          description:
            'One or more items in your cart have pending variations in the BOQ. These must be approved or denied before the order can be created',
        },
        imageUploadFailed: {
          title: 'Upload failed',
          description: 'Image upload failed. Please try again or use a different file.',
        },
        ruleViolation: {
          title: 'Rule violation',
          description: 'Validation rules are being violated',
        },
        invalidSpec: 'Invalid item spec',
        roleAlreadyAssigned: {
          title: 'Role already assigned',
          description: 'This user already has that role.',
        },
      },
    },
  },
  featureUnderConstruction: {
    title: 'Hard Hat Area',
    subTitle: 'This feature is under construction.',
    callToAction: 'Please check back later.',
  },
  notSupported: 'Functionality not supported on mobile',
  statuses: {
    status: 'Status',
    approved: 'Approved',
    awaitingProof: 'Awaiting proof of payment',
    cancelled: 'Cancelled',
    closed: 'Closed',
    complete: 'Complete',
    credit: 'Credit',
    current: 'Current',
    declined: 'Declined',
    delivered: 'Delivered',
    draft: 'Draft',
    expired: 'Expired',
    live: 'Live',
    na: 'NA',
    paid: 'Paid',
    partialPaid: 'Partial',
    partialDelivered: 'Partially Delivered',
    pending: 'Pending',
    planning: 'Planning',
    prsPending: 'PRs Pending',
    rejected: 'Rejected',
    requested: 'Requested',
    returned: 'Returned',
    settled: 'Settled',
    unpaid: 'Unpaid',
    verified: 'Verified',
  },
  documents: {
    title: 'Upload proof of payment',
    upload: 'Upload',
    uploadDocumentation: 'Upload Documentation',
    documents: 'Documents',
    downloadAll: 'Download all',
    emptyState: 'No documentation added',
    exceedsSizeLimit: 'This document exceeds the limit of {limit}',
    toasts: {
      successTitle: 'Document uploaded',
      successSubtitle: 'The document has been successfully uploaded',
      errorTitle: 'Error',
      errorSubtitleGeneric: 'An error occurred while uploading the document',
      errorSubtitleFileBlocked:
        "This file couldn't be uploaded. Please try another file, or if the issue persists, try saving it in a different format.",
    },
  },
  validation: {
    required: 'This is a required field',
    email: 'Must be a valid email address',
    toasts: {
      errorTitle: 'Error',
      errorSubtitle: 'An error occurred while processing your submission',
    },
  },
  search: {
    searching: 'Searching for {context}...',
    noResults: 'No results found',
  },
  addressComponent: {
    defaultLabel: 'Address',
    contextualLabel: 'Enter {context} address',
    contextualViewOnlyLabel: '{context} address',
    defaultSearchPlaceholder: 'Search for address',
    contextualSearchPlaceholder: 'Search for {context} address',
    addressType1: 'Site',
    addressType2: 'Vendor',
    addressType3: 'Organisation',
    addressType4: 'Delivery',
  },
  toast: {
    pinMessage: { message: 'Message has been pinned.', action: 'Click X to dismiss.' },
    durationMessage: {
      message: 'Message will close in {duration} seconds.',
      action: 'Click to stop.',
    },
  },
};
